import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { ITagTemplate } from "@formant/core/model/ITagTemplate";

import { routerStore, tagTemplateStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class TagTemplates extends Component {
    @localSettingObservable("settings-tags-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-tag-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<ITagTemplate>[] = [
            {
                header: "Tag key",
                row: _ => <RowTitle title={_.tagKey} icon="tags" />,
                sortBy: _ => _.tagKey,
            },
            {
                header: "Group",
                row: _ => Boolean(_.isGroup).toString(),
                sortBy: _ => Boolean(_.isGroup).toString(),
            },
            {
                header: "Telemetry filter",
                row: _ => (_.isTelemetryFilter ? "enabled" : "disabled"),
                sortBy: _ => Boolean(_.isTelemetryFilter).toString(),
            },
            {
                header: "Event filter",
                row: _ => (_.isEventFilter ? "enabled" : "disabled"),
                sortBy: _ => Boolean(_.isEventFilter).toString(),
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions tagTemplate={_} />,
            },
        ];

        const templates = tagTemplateStore.list();
        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions = [
            {
                dimensionKey: "isGroup",
                dimensionName: "Group",
                getValue: (_: ITagTemplate) => Boolean(_.isGroup).toString(),
            },
            {
                dimensionKey: "isTelemetryFilter",
                dimensionName: "Telemetry filter",
                getValue: (_: ITagTemplate) =>
                    _.isTelemetryFilter ? "enabled" : "disabled",
            },
            {
                dimensionKey: "isEventFilter",
                dimensionName: "Event filter",
                getValue: (_: ITagTemplate) =>
                    _.isEventFilter ? "enabled" : "disabled",
            },
        ];

        const filtered = applySimpleFilter(
            templates || [],
            dimensions,
            _ => _.tagKey,
            filter,
        ).filter(_ => !_.tagKey.startsWith("$."));

        return (
            <SettingsPage
                title="Tags"
                extra={
                    <Help
                        text="Tags are custom dimensions on telemetry and event data"
                        to="https://docs.formant.io/docs/scoping-down-access-to-specific-assets"
                    />
                }
                actions={
                    <StandardButton
                        data-testid="add-tag-button"
                        to={routeTo.createTagTemplate()}
                    >
                        Add tag
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="tags"
                    loading={!templates}
                    totalQuantity={templates?.length || 0}
                    totalLabel="Tags"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={templates || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filtered || []}
                        columns={columns}
                        loading={templates === undefined}
                        noResultsText="No tags found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                        rowTestId={_ => `tags-settings-${_.tagKey}`}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (tagTemplate: ITagTemplate) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editTagTemplate(
                { id: defined(tagTemplate.id) },
                { tab: "Tag" },
            ),
        );
    };
}
